import React, { useMemo, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeSanitize from "rehype-sanitize";
import { defaultSchema } from "hast-util-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import "katex/dist/katex.min.css";
import "./markdown.css";
import "../styles/chat-answer-typography.css";

type CodeProps = {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
};

function CodeBlock({ inline, className, children, ...props }: CodeProps) {
  const match = /language-(\w+)/.exec(className || "");
  const lang = match?.[1] || "";
  const raw = String(children ?? "").replace(/\n$/, "");
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // noop
    }
  }, [raw]);

  if (!inline && match) {
    return (
      <div style={{ position: "relative", margin: "1rem 0" }}>
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            display: "flex",
            gap: 6,
            alignItems: "center",
            padding: "2px 8px",
            fontSize: "0.75rem",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "#abb2bf",
            borderBottomLeftRadius: "5px",
            textTransform: "uppercase",
          }}
        >
          <span aria-label={`Code language: ${lang}`}>{lang}</span>
          <button
            onClick={handleCopy}
            aria-label="Copy code block"
            style={{
              background: "transparent",
              color: "inherit",
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <SyntaxHighlighter style={atomDark} language={lang} PreTag="div" {...props}>
          {raw}
        </SyntaxHighlighter>
      </div>
    );
  }

  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
}

export interface MarkdownRenderProps {
  children: string | React.ReactNode;
  allowHtml?: boolean; // not enabled by default for safety
  partialStreaming?: boolean; // hint for future tuning on incomplete fences
}

function buildSanitizeSchema() {
  // Start from default safe schema and extend for KaTeX + accessibility
  const base: any = defaultSchema || {};
  const tagNames = new Set<string>([...(base.tagNames || [])]);
  [
    "span",
    "div",
    // MathML used by KaTeX for accessibility
    "math","mrow","mi","mo","mn","ms","mtext","mspace","mstyle","mfrac","msqrt","mroot",
    "mtable","mtr","mtd","mover","munder","munderover","msub","msup","msubsup","mpadded",
    "mphantom","menclose","semantics","annotation","annotation-xml"
  ].forEach(t => tagNames.add(t));

  const attributes = { ...(base.attributes || {}) } as Record<string, any[]>;
  const all = new Set<any>([...((attributes["*"] as any[]) || [])]);
  [
    "className",
    "style",
    "aria-hidden",
    "aria-label",
    "role",
    ["data-*"],
  ].forEach(a => all.add(a));
  attributes["*"] = Array.from(all);
  attributes["math"] = [...(attributes["math"] || []), "xmlns"];
  attributes["annotation-xml"] = [...(attributes["annotation-xml"] || []), "encoding"];

  return { ...base, tagNames: Array.from(tagNames), attributes } as any;
}

const sanitizeSchema = buildSanitizeSchema();

function MarkdownRender({ children, allowHtml = false }: MarkdownRenderProps) {
  const markdownString = useMemo(
    () => (typeof children === "string" ? children : String(children ?? "")),
    [children]
  );

  // We do NOT enable raw HTML from Markdown (`rehype-raw`) for safety.
  // Instead, rely on react-markdown escaping and a sanitize pass post-KaTeX to keep math output intact.
  return (
    <div className="markdown-body answer">
      <ReactMarkdown
        remarkPlugins={[remarkGfm as any, remarkMath as any]}
        rehypePlugins={[
          rehypeKatex as any,
          // If a future use-case needs raw HTML, introduce rehype-raw before sanitize and tighten schema.
          [rehypeSanitize as any, sanitizeSchema],
        ]}
        components={{ code: CodeBlock as any }}
      >
        {markdownString}
      </ReactMarkdown>
    </div>
  );
}

// Improve React Refresh identification in dev
MarkdownRender.displayName = 'MarkdownRender';
const MarkdownRenderMemo = React.memo(MarkdownRender);
export default MarkdownRenderMemo;
